import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Loader2, ExternalLink } from 'lucide-react';
import { getImageUrl } from '../utils/api';
import { useSEO } from '../utils/seo';
import { ICON_MAP, getIcon } from './ArtistLinksPage';
import './PressKitPage.css';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const SOCIAL_KEY_MAP = {
  website_url: 'globe', website: 'globe',
  facebook_url: 'facebook', facebook: 'facebook',
  instagram_handle: 'instagram', instagram: 'instagram',
  youtube_channel: 'youtube', youtube: 'youtube',
  tiktok_username: 'tiktok', tiktok: 'tiktok',
  soundcloud_url: 'soundcloud', soundcloud: 'soundcloud',
  spotify_url: 'spotify', spotify: 'spotify',
  beatport_url: 'beatport', beatport: 'beatport',
  bandcamp_url: 'bandcamp', bandcamp: 'bandcamp',
  twitter: 'twitter', x_handle: 'twitter',
  apple_music_url: 'apple', apple_music: 'apple',
  amazon_music_url: 'link', deezer_url: 'link',
};

const parseShowDate = (dateStr) => {
  if (!dateStr) return { day: '', month: '', year: '' };
  try {
    const d = new Date(dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr);
    return {
      day: d.toLocaleDateString('en-US', { day: '2-digit' }),
      month: d.toLocaleDateString('en-US', { month: 'short' }).toUpperCase(),
      year: d.toLocaleDateString('en-US', { year: 'numeric' }),
    };
  } catch {
    return { day: '', month: dateStr, year: '' };
  }
};

const formatReleaseDate = (dateStr) => {
  if (!dateStr) return '';
  try {
    const d = new Date(dateStr.length === 10 ? `${dateStr}T12:00:00` : dateStr);
    return 'DATE: ' + d.toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

export default function PressKitPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [avatarFailed, setAvatarFailed] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchData = async () => {
      try {
        const res = await fetch(`${API_URL}/api/presskit/${slug}`);
        if (!res.ok) throw new Error(res.status === 404 ? 'Artist not found' : 'Failed to load press kit');
        setData(await res.json());
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  // SEO meta tags (must be before early returns)
  useSEO({
    title: data?.artist?.name ? `${data.artist.name} - Press Kit` : 'Press Kit',
    description: data?.artist ? `Electronic Press Kit for ${data.artist.name}. ${data.artist.genre || 'Electronic Music'} artist on Anyone Music Records.` : '',
    image: data?.artist?.hero_image || data?.artist?.profile_image || '',
    type: 'profile'
  });

  if (loading) {
    return (
      <div className="presskit-page">
        <div className="pk-loading"><Loader2 size={32} className="spinner" /><p>Loading press kit...</p></div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="presskit-page">
        <div className="pk-error"><p>{error || 'Press kit not found'}</p></div>
      </div>
    );
  }

  const { artist, releases, shows, gallery } = data;
  const heroImg = getImageUrl(artist.hero_image) || getImageUrl(artist.profile_image);
  const avatarImg = getImageUrl(artist.profile_image);
  const location = [artist.city, artist.country].filter(Boolean).join(', ');
  const hasStats = artist.years_experience || artist.events_played || artist.hours_mixed;
  const pressPhotos = (artist.press_photos || []).filter(Boolean).map(p => getImageUrl(p)).filter(Boolean);
  const galleryPhotos = (gallery || []).filter(Boolean).map(p => getImageUrl(p)).filter(Boolean);
  const allPhotos = [...galleryPhotos, ...pressPhotos];
  const socialEntries = Object.entries(artist.social_links || {}).filter(([, v]) => v);

  const sections = [];
  if (artist.bio) sections.push({ id: 'bio', label: 'Bio' });
  if (allPhotos.length > 0) sections.push({ id: 'gallery', label: 'Gallery' });
  if (shows?.length > 0) sections.push({ id: 'events', label: 'Events' });
  if (releases?.length > 0) sections.push({ id: 'releases', label: 'Releases' });
  if (artist.tech_rider) sections.push({ id: 'rider', label: 'Rider' });
  if (socialEntries.length > 0) sections.push({ id: 'connect', label: 'Connect' });

  return (
    <div className="presskit-page" data-testid="presskit-page">
      {/* ═══ HERO — full viewport ═══ */}
      <div className="pk-hero" data-testid="pk-hero">
        <div className="pk-hero-bg" style={heroImg ? { backgroundImage: `url(${heroImg})` } : {}} />
        <div className="pk-hero-overlay" />
        <div className="pk-hero-content">
          {avatarImg && !avatarFailed ? (
            <img src={avatarImg} alt={artist.name} className="pk-avatar" onError={() => setAvatarFailed(true)} />
          ) : (
            <div className="pk-avatar-placeholder">{artist.name?.charAt(0)}</div>
          )}

          <h1 className="pk-hero-name" data-testid="pk-artist-name">{artist.name}</h1>

          {/* Stats in hero like reference */}
          {hasStats && (
            <div className="pk-hero-stats" data-testid="pk-stats">
              {artist.years_experience && (
                <div className="pk-hero-stat">
                  <div className="pk-hero-stat-number">+{artist.years_experience}</div>
                  <div className="pk-hero-stat-label">years of experience</div>
                </div>
              )}
              {artist.events_played && (
                <div className="pk-hero-stat">
                  <div className="pk-hero-stat-number">+{artist.events_played}</div>
                  <div className="pk-hero-stat-label">events performed</div>
                </div>
              )}
              {artist.hours_mixed && (
                <div className="pk-hero-stat">
                  <div className="pk-hero-stat-number">+{artist.hours_mixed}</div>
                  <div className="pk-hero-stat-label">minutes mixed</div>
                </div>
              )}
            </div>
          )}

          {/* Info bar — location, genre */}
          <div className="pk-info-bar">
            {location && (
              <div className="pk-info-item">
                <span className="pk-info-item-label">Location</span>
                <span className="pk-info-item-value">{location}</span>
              </div>
            )}
            {artist.genre && (
              <div className="pk-info-item">
                <span className="pk-info-item-label">Genres</span>
                <span className="pk-info-item-value">{artist.genre}</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ═══ STICKY NAV ═══ */}
      <nav className="pk-sticky-nav" data-testid="pk-nav">
        <div className="pk-nav-links">
          {sections.map(s => (
            <a key={s.id} href={`#${s.id}`} className="pk-nav-link" onClick={e => {
              e.preventDefault();
              document.getElementById(s.id)?.scrollIntoView({ behavior: 'smooth' });
            }}>{s.label}</a>
          ))}
        </div>
      </nav>

      {/* ═══ CONTENT ═══ */}
      <div className="pk-content">

        {/* ── BIO ── */}
        {artist.bio && (
          <section className="pk-section" id="bio" data-testid="pk-bio">
            <span className="pk-section-tag">BIO</span>
            <h2 className="pk-section-title">about me</h2>
            <div className="pk-bio-layout">
              <div>
                <p className="pk-bio-text">{artist.bio}</p>
                <p className="pk-bio-note">Part of the Anyone Music Records family</p>
              </div>
            </div>
            {/* Press photos gallery */}
            {pressPhotos.length > 0 && !allPhotos.length && (
              <div className="pk-gallery" data-testid="pk-gallery">
                {pressPhotos.map((photo, i) => (
                  <img key={i} src={photo} alt={`${artist.name} gallery ${i + 1}`} onError={e => { e.target.style.display = 'none'; }} />
                ))}
              </div>
            )}
          </section>
        )}

        {/* ── GALLERY — Media Vault photos ── */}
        {allPhotos.length > 0 && (
          <section className="pk-section" id="gallery" data-testid="pk-media-gallery">
            <span className="pk-section-tag">Gallery</span>
            <h2 className="pk-section-title">photos</h2>
            <div className="pk-gallery">
              {allPhotos.map((photo, i) => (
                <img key={i} src={photo} alt={`${artist.name} photo ${i + 1}`} onError={e => { e.target.style.display = 'none'; }} />
              ))}
            </div>
          </section>
        )}

        {/* ── EVENTS — calendar block style ── */}
        {shows?.length > 0 && (
          <section className="pk-section" id="events" data-testid="pk-shows">
            <span className="pk-section-tag">Events</span>
            <h2 className="pk-section-title">upcoming shows</h2>
            <div className="pk-events-list">
              {shows.map((s, i) => {
                const { day, month } = parseShowDate(s.date);
                return (
                  <div key={i} className="pk-event-item">
                    <div className="pk-event-date-block">
                      <div className="pk-event-day">{day}</div>
                      <div className="pk-event-month">{month}</div>
                    </div>
                    <div className="pk-event-info">
                      <div className="pk-event-title">{s.title}</div>
                      <div className="pk-event-venue-label">VENUE</div>
                      <div className="pk-event-venue">{[s.venue, s.city].filter(Boolean).join(', ')}</div>
                    </div>
                    {s.external_link && (
                      <a href={s.external_link} target="_blank" rel="noopener noreferrer" className="pk-event-ticket">
                        BUY TICKETS &gt;
                      </a>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        )}

        {/* ── RELEASES — horizontal scroll ── */}
        {releases?.length > 0 && (
          <section className="pk-section" id="releases" data-testid="pk-releases">
            <span className="pk-section-tag">Latest releases</span>
            <h2 className="pk-section-title">the new</h2>
            <div className="pk-releases-scroll">
              {releases.map((r, i) => {
                const card = (
                  <div key={i} className="pk-release-card">
                    {r.artwork ? (
                      <img src={getImageUrl(r.artwork) || ''} alt={r.title} className="pk-release-artwork" onError={e => { e.target.style.display = 'none'; }} />
                    ) : (
                      <div className="pk-release-artwork" />
                    )}
                    <div className="pk-release-info">
                      <p className="pk-release-title">{r.title}</p>
                      <p className="pk-release-label">{r.label}</p>
                      <p className="pk-release-date">{formatReleaseDate(r.release_date)}</p>
                    </div>
                  </div>
                );
                return r.stream_link ? (
                  <a key={i} href={r.stream_link} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: 'inherit' }}>
                    {card}
                  </a>
                ) : card;
              })}
            </div>
          </section>
        )}

        {/* ── RIDER ── */}
        {artist.tech_rider && (
          <section className="pk-section" id="rider" data-testid="pk-rider">
            <span className="pk-section-tag">Technical Rider</span>
            <h2 className="pk-section-title">setup</h2>
            <pre className="pk-rider-block">{artist.tech_rider}</pre>
          </section>
        )}

        {/* ── CONNECT ── */}
        {socialEntries.length > 0 && (
          <section className="pk-section" id="connect" data-testid="pk-socials">
            <span className="pk-section-tag">Social</span>
            <h2 className="pk-section-title">connect</h2>
            <div className="pk-social-grid">
              {socialEntries.map(([key, url]) => {
                const iconKey = SOCIAL_KEY_MAP[key] || 'link';
                const label = key.replace(/_url$/, '').replace(/_handle$/, '').replace(/_/g, ' ');
                return (
                  <a key={key} href={url} target="_blank" rel="noopener noreferrer" className="pk-social-btn">
                    {getIcon(iconKey)}
                    <span>{label}</span>
                  </a>
                );
              })}
            </div>
          </section>
        )}
      </div>

      {/* ═══ FOOTER ═══ */}
      <footer className="pk-footer" data-testid="pk-footer">
        <img src="/amr-logo.png" alt="AMR" className="pk-footer-logo" />
        <a href="https://anyonemusicrecords.com" target="_blank" rel="noopener noreferrer" className="pk-footer-link">
          anyonemusicrecords.com
        </a>
        <p className="pk-footer-note">Artist represented by Anyone Music Records</p>
      </footer>
    </div>
  );
}
